import { mountPpfForm } from "./ppf-form";
import { h, type Child } from "../../shared/safe";
import {
  blankProject,blankReleaseDate,
  MIN_AREA_WORDS,
  blankArea, blankAuthor, blankGuideLink, blankItem, blankListing, blankNameDesc, blankReference, blankTopic, blankWikiMeta,
  fromSupplied, hasEntries, mapServerPath, toSupplied, validateSupplied, wordCount,
  type Choices, type FieldErrors, type SuppliedDraft, type SuppliedKey,
} from "./model";

// The guided form for the entries an owner supplies (directory listings, wiki references and related entries, area guides, authors, magazine formats).
// Rows are cards with Add / Remove; choices that must match something entered elsewhere (a listing's category, an entry's references) are pickers fed from
// what is on the page, never free text. Typing never rebuilds the form (focus stays); only add, remove and import do. Every value lives in the draft the
// Site screen owns, so a failed compose leaves everything entered exactly where it was. Nothing is pre-filled and no example is ever inserted.

export interface SuppliedFormOptions {
  siteId: string;
  key: SuppliedKey;
  required: boolean;
  description: string;
  draft: SuppliedDraft;
  choices: Choices;
  /** Problems the composer sent back for `supplied...` fields, with the server's own paths. */
  serverProblems: { field: string; message: string }[];
}
export interface SuppliedFormHandle {
  el: HTMLElement;
  /** Shows inline validation from now on and returns what is wrong (empty when the entries can be sent). */
  validate(): FieldErrors;
  /** Focuses the first field that has a problem. */
  focusFirstProblem(): void;
  /** Refreshes image pickers when the owner changes the site's pinned images. */
  updateMediaChoices?(media: NonNullable<Choices["media"]>): void;
}

const uidBase = (o: SuppliedFormOptions) => `sk-sup-${o.siteId}`;

export function mountSuppliedForm(o: SuppliedFormOptions): SuppliedFormHandle {
  if(o.key === "ppf") return mountPpfForm(o);
  const { draft, key } = o;
  const root = h("div", { class: "sk-supplied", dataset: { supplied: key, guided: "true" } });
  let revealed = o.serverProblems.length > 0;
  let errors: FieldErrors = {};
  const slots = new Map<string, HTMLElement>();
  const controls = new Map<string, HTMLElement>();
  const serverErrors = new Map<string, string>();
  for (const p of o.serverProblems) { const path = mapServerPath(p.field, key, draft); serverErrors.set(path, `${serverErrors.get(path) ?? ""}${serverErrors.has(path) ? " " : ""}${p.message}`); }
  const base = uidBase(o);
  let jsonView: HTMLTextAreaElement | null = null;
  let importNote: HTMLElement | null = null;

  // ---- errors: patched in place so typing never loses focus ----------------------------------------------------------------------
  const messageFor = (path: string): string => (revealed ? errors[path] : undefined) ?? serverErrors.get(path) ?? "";
  function refresh() {
    errors = validateSupplied(key, draft, o.required);
    for (const [path, slot] of slots) {
      const msg = messageFor(path);
      slot.textContent = msg;
      slot.hidden = msg === "";
      const c = controls.get(path);
      if (c) { if (msg) c.setAttribute("aria-invalid", "true"); else c.removeAttribute("aria-invalid"); }
    }
    if (jsonView) jsonView.value = JSON.stringify(toSupplied(key, draft) ?? {}, null, 2);
    for (const el of root.querySelectorAll<HTMLElement>("[data-sync]")) el.dispatchEvent(new Event("sk-sync"));
  }
  const edited = (path?: string) => { if (path) serverErrors.delete(path); refresh(); };

  // ---- field builders -------------------------------------------------------------------------------------------------------------
  let n = 0;
  const nid = (s: string) => `${base}-${s}-${++n}`;
  const slot = (path: string, id: string): HTMLElement => {
    const el = h("div", { class: "fx-error", id: `${id}-err`, role: "alert", hidden: true });
    slots.set(path, el);
    return el;
  };
  function field(path: string, label: string, control: HTMLElement, o2: { help?: string; optional?: boolean; wide?: boolean } = {}): HTMLElement {
    const id = nid("f");
    control.id = id;
    controls.set(path, control);
    const err = slot(path, id);
    const desc = [o2.help ? `${id}-help` : "", `${id}-err`].filter(Boolean).join(" ");
    control.setAttribute("aria-describedby", desc);
    return h(
      "div",
      { class: `fx-field${o2.wide ? " fx-field--wide" : ""}`, dataset: { fieldPath: path } },
      h("label", { for: id }, label, o2.optional ? h("span", { class: "sk-opt" }, " (optional)") : h("span", { class: "sk-req" }, " (required)")),
      control,
      o2.help ? h("div", { class: "fx-help", id: `${id}-help` }, o2.help) : null,
      err,
    );
  }
  const text = (get: () => string, set: (v: string) => void, path: string, o2: { type?: string; placeholder?: string; after?: (oldV: string, newV: string) => void } = {}): HTMLInputElement => {
    const el = h("input", { type: o2.type ?? "text", autocomplete: "off", spellcheck: "false", placeholder: o2.placeholder, dataset: { path } }) as HTMLInputElement;
    el.value = get();
    el.addEventListener("input", () => { const old = get(); set(el.value); o2.after?.(old, el.value); edited(path); });
    return el;
  };
  const area = (get: () => string, set: (v: string) => void, path: string, rows = 3, placeholder?: string): HTMLTextAreaElement => {
    const el = h("textarea", { rows, spellcheck: "false", placeholder, dataset: { path } }) as HTMLTextAreaElement;
    el.value = get();
    el.addEventListener("input", () => { set(el.value); edited(path); });
    return el;
  };
  /** A select whose options are read from `options()` each time the form changes, keeping the chosen value even if it vanished (the error then says so). */
  const picker = (get: () => string, set: (v: string) => void, path: string, options: () => string[] | { value: string; label: string }[], blank: string): HTMLSelectElement => {
    const el = h("select", { dataset: { path, sync: "true" } }) as HTMLSelectElement;
    let sig = "\u0000";
    const fill = () => {
      const opts = options().map((x) => (typeof x === "string" ? { value: x, label: x } : x));
      const cur = get();
      const next = JSON.stringify([opts, cur]);
      if (next === sig) return;
      sig = next;
      el.replaceChildren(h("option", { value: "" }, blank), ...opts.map((x) => h("option", { value: x.value }, x.label)));
      if (cur !== "" && !opts.some((x) => x.value === cur)) el.append(h("option", { value: cur }, `${cur} (no longer available)`));
      el.value = cur;
    };
    fill();
    el.addEventListener("sk-sync", fill);
    el.addEventListener("change", () => { set(el.value); edited(path); });
    return el;
  };
  /** A set of tick boxes (what a row refers to). Options follow the form as it changes. */
  const ticks = (legend: string, get: () => string[], set: (v: string[]) => void, path: string, options: () => { value: string; label: string }[], empty: string, o2: { wide?: boolean } = {}): HTMLElement => {
    const id = nid("g");
    const fs = h("fieldset", { class: "sk-ticks", id, dataset: { path, sync: "true" } }, h("legend", null, legend, h("span", { class: "sk-opt" }, " (optional)")));
    controls.set(path, fs);
    const err = slot(path, id);
    fs.setAttribute("aria-describedby", `${id}-err`);
    let sig = "\u0000";
    const fill = () => {
      const opts = options();
      const cur = get();
      const next = JSON.stringify([opts, cur]);
      if (next === sig) return;
      const onlyChecks = sig !== "\u0000" && JSON.stringify(opts) === JSON.stringify(JSON.parse(sig)[0]);
      sig = next;
      if (onlyChecks) { for (const cb of fs.querySelectorAll<HTMLInputElement>("input[type=checkbox]")) cb.checked = cur.includes(cb.value); return; }
      const gone = cur.filter((v) => !opts.some((x) => x.value === v));
      const all = [...opts, ...gone.map((v) => ({ value: v, label: `${v} (no longer available)` }))];
      fs.replaceChildren(fs.querySelector("legend")!, ...(all.length === 0 ? [h("div", { class: "fx-help" }, empty)] : all.map((x) => {
        const cb = h("input", { type: "checkbox", value: x.value }) as HTMLInputElement;
        cb.checked = cur.includes(x.value);
        cb.addEventListener("change", () => { const now = get().filter((v) => v !== x.value); if (cb.checked) now.push(x.value); set(now); edited(path); });
        return h("label", { class: "sk-tick" }, cb, " ", x.label);
      })), err);
    };
    fill();
    fs.addEventListener("sk-sync", fill);
    return h("div", { class: `fx-field${o2.wide ? " fx-field--wide" : ""}` }, fs);
  };

  // ---- repeatable rows ------------------------------------------------------------------------------------------------------------
  let focusAfter: string | null = null;
  function rows<T>(o2: { path: string; title: string; noun: string; list: T[]; blank: () => T; addLabel: string; hint?: string; required?: boolean; name: (r: T) => string; body: (r: T, i: number) => Child[] }): HTMLElement {
    const sectionId = nid("sec");
    const secErr = slot(o2.path, sectionId);
    const section = h(
      "section",
      { class: "sk-section", "aria-labelledby": `${sectionId}-h`, dataset: { section: o2.path } },
      h("h5", { id: `${sectionId}-h` }, o2.title, o2.required ? h("span", { class: "sk-req" }, " (required)") : h("span", { class: "sk-opt" }, " (optional)")),
      o2.hint ? h("p", { class: "fx-help" }, o2.hint) : null,
      secErr,
      o2.list.length === 0 ? h("p", { class: "fx-help sk-none" }, `No ${o2.noun}s added yet.`) : null,
      ...o2.list.map((r, i) => {
        const label = `${o2.noun[0]!.toUpperCase()}${o2.noun.slice(1)} ${i + 1}`;
        const named = o2.name(r).trim();
        return h(
          "fieldset",
          { class: "sk-row", dataset: { row: `${o2.path}.${i}` } },
          h("legend", null, label, named ? `: ${named.slice(0, 40)}` : ""),
          h("div", { class: "fx-grid" }, ...o2.body(r, i)),
          h("button", { type: "button", class: "secondary sk-remove", dataset: { action: "remove-row", section: o2.path, index: i }, "aria-label": `Remove ${label.toLowerCase()}${named ? `: ${named.slice(0, 40)}` : ""}`, on: { click: () => { o2.list.splice(i, 1); focusAfter = `[data-action="add-row"][data-section="${o2.path}"]`; render(); } } }, `Remove ${o2.noun}`),
        );
      }),
      h("button", { type: "button", class: "secondary", dataset: { action: "add-row", section: o2.path }, on: { click: () => { o2.list.push(o2.blank()); focusAfter = `[data-row="${o2.path}.${o2.list.length - 1}"] input, [data-row="${o2.path}.${o2.list.length - 1}"] select, [data-row="${o2.path}.${o2.list.length - 1}"] textarea`; render(); } } }, o2.addLabel),
    );
    return section;
  }

  // ---- choices fed from the page -------------------------------------------------------------------------------------------------
  const contentOpts = () => o.choices.content.map((c) => ({ value: c.id, label: c.title }));
  const serviceOpts = () => o.choices.services.map((s) => ({ value: s, label: s }));
  const names = (list: { name: string }[]) => () => list.map((x) => x.name.trim()).filter(Boolean).map((v) => ({ value: v, label: v }));
  const noContent = o.choices.content.length === 0 ? "This client has no approved content yet. Approve some in the content area first, then choose it here." : "";
  const topicList = [...new Set(o.choices.content.map((c) => c.topic).filter((x): x is string => !!x))];

  /** When a name that other rows point at is edited, the rows that pointed at the old name follow it. */
  const rename = (oldV: string, newV: string, targets: { list: unknown[]; field: string; multi?: boolean }[]) => {
    if (oldV.trim() === "") return;
    for (const tg of targets) for (const r of tg.list as Record<string, unknown>[]) {
      if (tg.multi) { const a = r[tg.field] as string[]; const i = a.indexOf(oldV.trim()); if (i !== -1) a[i] = newV.trim(); }
      else if (r[tg.field] === oldV.trim()) r[tg.field] = newV.trim();
    }
  };

  const authorsSection = (hint: string) => rows({
    path: "authors", title: "Authors", noun: "author", list: draft.authors, blank: blankAuthor, addLabel: "Add an author", hint, name: (r) => r.name,
    body: (a, i) => [
      field(`authors.${i}.name`, "Name", text(() => a.name, (v) => (a.name = v), `authors.${i}.name`), {}),
      field(`authors.${i}.bio`, "Short biography", area(() => a.bio, (v) => (a.bio = v), `authors.${i}.bio`, 2), { optional: true, wide: true, help: "Shown on the author's page exactly as written." }),
      ticks("Content this author wrote", () => a.contentIds, (v) => (a.contentIds = v), `authors.${i}.masterIds`, contentOpts, noContent || "No approved content to choose from.", { wide: true }),
    ],
  });
  const topicsSection = () => rows({
    path: "topics", title: "Topic descriptions", noun: "topic description", list: draft.topics, blank: blankTopic, addLabel: "Add a topic description", name: (r) => r.topic,
    hint: "Without one, a topic page uses a fixed label such as \"Entries about <topic>\". Write your own here for any topic.",
    body: (tp, i) => {
      const dl = h("datalist", { id: `${base}-topics-${i}` }, ...topicList.map((x) => h("option", { value: x })));
      const input = text(() => tp.topic, (v) => (tp.topic = v), `topics.${i}.topic`);
      input.setAttribute("list", dl.id);
      return [field(`topics.${i}.topic`, "Topic", input, { help: topicList.length ? "Pick one of this client's topics or type its exact name." : "Type the topic's exact name." }), dl, field(`topics.${i}.description`, "Description", text(() => tp.description, (v) => (tp.description = v), `topics.${i}.description`), {})];
    },
  });

  // ---- one section group per kit --------------------------------------------------------------------------------------------------
  function sections(): HTMLElement[] {
    if (key === "authors") return [authorsSection("Without authors no byline and no author page exists. An article is credited only to an author you list here."), topicsSection()];
    if (key === "directory") return [
      rows({
        path: "categories", title: "Categories", noun: "category", list: draft.categories, blank: blankNameDesc, addLabel: "Add a category", required: o.required, name: (r) => r.name,
        hint: "Every listing belongs to one category. Each category gets its own page.",
        body: (c, i) => [
          field(`categories.${i}.name`, "Category name", text(() => c.name, (v) => (c.name = v), `categories.${i}.name`, { after: (a, b) => rename(a, b, [{ list: draft.listings, field: "category" }]) })),
          field(`categories.${i}.description`, "What this category covers", text(() => c.description, (v) => (c.description = v), `categories.${i}.description`)),
        ],
      }),
      rows({
        path: "places", title: "Areas", noun: "area", list: draft.places, blank: blankNameDesc, addLabel: "Add an area", name: (r) => r.name,
        hint: "Optional. Add areas only if listings should be grouped by place. Without any, no area pages or filter appear.",
        body: (c, i) => [
          field(`places.${i}.name`, "Area name", text(() => c.name, (v) => (c.name = v), `places.${i}.name`, { after: (a, b) => rename(a, b, [{ list: draft.listings, field: "place" }]) })),
          field(`places.${i}.description`, "About this area", text(() => c.description, (v) => (c.description = v), `places.${i}.description`)),
        ],
      }),
      rows({
        path: "listings", title: "Listings", noun: "listing", list: draft.listings, blank: blankListing, addLabel: "Add a listing", required: o.required, name: (r) => r.name,
        hint: "A directory contains exactly the entries you add here, with exactly the details you give. Real, approved businesses only: this is not read from the client's other records.",
        body: (l, i) => [
          field(`listings.${i}.name`, "Business name", text(() => l.name, (v) => (l.name = v), `listings.${i}.name`)),
          field(`listings.${i}.category`, "Category", picker(() => l.category, (v) => (l.category = v), `listings.${i}.category`, names(draft.categories), "Choose a category…")),
          field(`listings.${i}.place`, "Area", picker(() => l.place, (v) => (l.place = v), `listings.${i}.place`, names(draft.places), "No area"), { optional: true }),
          field(`listings.${i}.summary`, "One-line summary", text(() => l.summary, (v) => (l.summary = v), `listings.${i}.summary`), { wide: true }),
          field(`listings.${i}.description`, "Description", area(() => l.description, (v) => (l.description = v), `listings.${i}.description`, 4), { wide: true, help: "Separate paragraphs with a blank line." }),
          field(`listings.${i}.offerings`, "What they offer", area(() => l.offerings, (v) => (l.offerings = v), `listings.${i}.offerings`, 3), { optional: true, help: "One per line." }),
          field(`listings.${i}.phone`, "Phone", text(() => l.phone, (v) => (l.phone = v), `listings.${i}.phone`, { type: "tel" }), { optional: true }),
          field(`listings.${i}.email`, "Email", text(() => l.email, (v) => (l.email = v), `listings.${i}.email`, { type: "email" }), { optional: true }),
          field(`listings.${i}.addressLine`, "Address", text(() => l.addressLine, (v) => (l.addressLine = v), `listings.${i}.addressLine`), { optional: true }),
          field(`listings.${i}.websiteUrl`, "Website", text(() => l.websiteUrl, (v) => (l.websiteUrl = v), `listings.${i}.websiteUrl`, { type: "url", placeholder: "https://" }), { optional: true, help: "Becomes an approved link target; nothing else is linked." }),
          field(`listings.${i}.confirmedOn`, "Details last confirmed", text(() => l.confirmedOn, (v) => (l.confirmedOn = v), `listings.${i}.confirmedOn`, { placeholder: "YYYY-MM-DD" }), { optional: true, help: "Shown only if you give a date." }),
          h("div", { class: "fx-field" }, h("label", { class: "sk-tick" }, (() => { const cb = h("input", { type: "checkbox" }) as HTMLInputElement; cb.checked = l.featured; cb.addEventListener("change", () => { l.featured = cb.checked; edited(); }); return cb; })(), " Feature this listing")),
        ],
      }),
    ];
    if (key === "wiki") return [
      rows({
        path: "references", title: "References", noun: "reference", list: draft.references, blank: blankReference, addLabel: "Add a reference", name: (r) => r.label,
        hint: "Sources you approve. A reference appears only on the entries that list it; an entry that lists none says so, and nothing is cited by default.",
        body: (r, i) => [
          field(`references.${i}.label`, "Label", text(() => r.label, (v) => (r.label = v), `references.${i}.label`, { after: (a, b) => rename(a, b, [{ list: draft.wikiEntries, field: "references", multi: true }]) })),
          field(`references.${i}.detail`, "Detail", text(() => r.detail, (v) => (r.detail = v), `references.${i}.detail`), { optional: true }),
          field(`references.${i}.url`, "Link", text(() => r.url, (v) => (r.url = v), `references.${i}.url`, { type: "url", placeholder: "https://" }), { optional: true, wide: true, help: "A reference can link only to an address you give here." }),
        ],
      }),
      rows({
        path: "wikiEntries", title: "Details for entries", noun: "entry", list: draft.wikiEntries, blank: blankWikiMeta, addLabel: "Add details for an entry", name: (r) => o.choices.content.find((c) => c.id === r.contentId)?.title ?? "",
        hint: "Optional. Choose an approved entry, then say which references it lists, which other entries are related and when it was last updated.",
        body: (m, i) => [
          field(`wikiEntries.${i}.contentId`, "Entry", picker(() => m.contentId, (v) => (m.contentId = v), `wikiEntries.${i}.contentId`, contentOpts, "Choose an entry…"), { help: noContent || undefined }),
          field(`wikiEntries.${i}.updatedOn`, "Last updated", text(() => m.updatedOn, (v) => (m.updatedOn = v), `wikiEntries.${i}.updatedOn`, { placeholder: "YYYY-MM-DD" }), { optional: true }),
          ticks("References this entry lists", () => m.references, (v) => (m.references = v), `wikiEntries.${i}.references`, names(draft.references.map((r) => ({ name: r.label }))), "Add a reference above to choose it here.", { wide: true }),
          ticks("Related entries", () => m.related, (v) => (m.related = v), `wikiEntries.${i}.related`, () => contentOpts().filter((c) => c.value !== m.contentId), noContent || "No other approved entries.", { wide: true }),
        ],
      }),
      topicsSection(),
    ];
    if(key==='cases')return [rows({
      path:'projects',title:'Approved project stories',noun:'project',list:draft.projects,blank:blankProject,addLabel:'Add a project',required:true,name:r=>r.title,
      hint:'Describe only the approved work and reported outcome. Nothing is generated or presented as independently verified. Add supporting evidence only when approved.',
      body:(p,i)=>[
        field(`projects.${i}.title`,'Project title',text(()=>p.title,v=>p.title=v,`projects.${i}.title`)),
        field(`projects.${i}.summary`,'Short summary',area(()=>p.summary,v=>p.summary=v,`projects.${i}.summary`,2),{wide:true}),
        field(`projects.${i}.challenge`,'The challenge',area(()=>p.challenge,v=>p.challenge=v,`projects.${i}.challenge`,4),{wide:true}),
        field(`projects.${i}.approach`,'The approach',area(()=>p.approach,v=>p.approach=v,`projects.${i}.approach`,4),{wide:true}),
        field(`projects.${i}.outcome`,'Reported outcome',area(()=>p.outcome,v=>p.outcome=v,`projects.${i}.outcome`,4),{wide:true,help:'Use the actual approved description. Do not invent metrics, endorsements or ranking results.'}),
        field(`projects.${i}.proofUrl`,'Supporting evidence URL',text(()=>p.proofUrl,v=>p.proofUrl=v,`projects.${i}.proofUrl`),{optional:true,wide:true}),
        field(`projects.${i}.completedOn`,'Completion date',text(()=>p.completedOn,v=>p.completedOn=v,`projects.${i}.completedOn`,{placeholder:'YYYY-MM-DD'}),{optional:true}),
      ],
    })];
    if(key==='newsroom')return [rows({
      path:'releases',title:'Approved release dates',noun:'release',list:draft.releases,blank:blankReleaseDate,addLabel:'Add a release date',required:true,name:r=>o.choices.content.find(c=>c.id===r.contentId)?.title??'',
      hint:'Every approved content article used on this site needs its actual issue date. The creation date in CloudPush is not substituted.',
      body:(r,i)=>[
        field(`releases.${i}.contentId`,'Approved release',picker(()=>r.contentId,v=>r.contentId=v,`releases.${i}.contentId`,contentOpts,'Choose a release…'),{wide:true,help:noContent||undefined}),
        field(`releases.${i}.issuedOn`,'Actual issue date',text(()=>r.issuedOn,v=>r.issuedOn=v,`releases.${i}.issuedOn`,{placeholder:'YYYY-MM-DD'})),
      ],
    })];
    if (key === "guide") return [
      rows({
        path: "areas", title: "Service areas", noun: "area", list: draft.areas, blank: blankArea, addLabel: "Add a service area", required: o.required, name: (r) => r.name,
        hint: `Each area gets its own page, written for that place. At least ${MIN_AREA_WORDS} words of text that say something specific to it are needed: access, timing, what is different there, who is served. Near-duplicate text between areas is refused.`,
        body: (a, i) => {
          const counter = h("div", { class: "fx-help sk-count", dataset: { count: "words" } });
          const body = area(() => a.body, (v) => { a.body = v; show(); }, `areas.${i}.body`, 8);
          const show = () => { const w = wordCount(a.body); counter.textContent = `${w} of ${MIN_AREA_WORDS} words`; };
          show();
          return [
            field(`areas.${i}.name`, "Area name", text(() => a.name, (v) => (a.name = v), `areas.${i}.name`, { after: (x, y) => rename(x, y, [{ list: draft.guideLinks, field: "areas", multi: true }]) })),
            field(`areas.${i}.summary`, "Short summary", text(() => a.summary, (v) => (a.summary = v), `areas.${i}.summary`)),
            field(`areas.${i}.body`, "Guide text for this area", body, { wide: true, help: "Separate paragraphs with a blank line. Start a line with \"- \" for a list." }),
            counter,
            ticks("Services offered here", () => a.services, (v) => (a.services = v), `areas.${i}.services`, serviceOpts, "This client has no approved services. Add them in the client's facts first.", { wide: true }),
            field(`areas.${i}.nearby`, "Nearby places or landmarks", area(() => a.nearby, (v) => (a.nearby = v), `areas.${i}.nearby`, 2), { optional: true, wide: true, help: "One per line." }),
          ];
        },
      }),
      rows({
        path: "guideLinks", title: "Link resources to a service or area", noun: "resource", list: draft.guideLinks, blank: blankGuideLink, addLabel: "Link a resource", name: (r) => o.choices.content.find((c) => c.id === r.contentId)?.title ?? "",
        hint: "Optional. Approved content appears as a resource on the service or area pages you choose here, and nowhere else.",
        body: (g, i) => [
          field(`guideLinks.${i}.contentId`, "Resource", picker(() => g.contentId, (v) => (g.contentId = v), `guideLinks.${i}.contentId`, contentOpts, "Choose a resource…"), { wide: true, help: noContent || undefined }),
          ticks("Services", () => g.services, (v) => (g.services = v), `guideLinks.${i}.services`, serviceOpts, "This client has no approved services.", { wide: true }),
          ticks("Service areas", () => g.areas, (v) => (g.areas = v), `guideLinks.${i}.areas`, names(draft.areas), "Add a service area above to choose it here.", { wide: true }),
        ],
      }),
    ];
    return [
      rows({
        path: "items", title: "Item labels", noun: "item", list: draft.items, blank: blankItem, addLabel: "Label an item", name: (r) => o.choices.content.find((c) => c.id === r.contentId)?.title ?? "",
        hint: "Optional. An item is an \"Article\" unless you label it a guide or resource here. A featured item is shown first on the home page.",
        body: (m, i) => [
          field(`items.${i}.contentId`, "Item", picker(() => m.contentId, (v) => (m.contentId = v), `items.${i}.contentId`, contentOpts, "Choose an item…"), { wide: true, help: noContent || undefined }),
          field(`items.${i}.format`, "Format", (() => {
            const el = h("select", { dataset: { path: `items.${i}.format` } }, h("option", { value: "" }, "Article (default)"), h("option", { value: "article" }, "Article"), h("option", { value: "guide" }, "Guide"), h("option", { value: "resource" }, "Resource")) as HTMLSelectElement;
            el.value = m.format; el.addEventListener("change", () => { m.format = el.value as typeof m.format; edited(); });
            return el;
          })(), { optional: true }),
          h("div", { class: "fx-field" }, h("label", { class: "sk-tick" }, (() => { const cb = h("input", { type: "checkbox" }) as HTMLInputElement; cb.checked = m.featured; cb.addEventListener("change", () => { m.featured = cb.checked; edited(); }); return cb; })(), " Feature this item")),
        ],
      }),
      authorsSection("Optional. Without authors no byline and no author page exists. An item is credited only to an author you list here."),
      topicsSection(),
    ];
  }

  // ---- advanced: view and import JSON ---------------------------------------------------------------------------------------------
  function advanced(): HTMLElement {
    jsonView = h("textarea", { rows: 8, readonly: true, class: "fx-mono", id: `${base}-json`, dataset: { field: "supplied-json" } }) as HTMLTextAreaElement;
    const input = h("textarea", { rows: 6, class: "fx-mono", spellcheck: "false", "aria-label": "JSON to import", dataset: { field: "supplied-import" } }) as HTMLTextAreaElement;
    const replaceBox = h("input", { type: "checkbox", id: `${base}-replace` }) as HTMLInputElement;
    const replaceRow = h("label", { class: "sk-tick" }, replaceBox, " Replace what I have entered above with this JSON");
    importNote = h("div", { class: "fx-help", role: "status", "aria-live": "polite" });
    const go = h("button", { type: "button", class: "secondary", dataset: { action: "import-json" }, on: { click: () => {
      let v: unknown;
      try { v = JSON.parse(input.value); } catch { importNote!.textContent = "That is not valid JSON, so nothing was imported. Check commas, quotes and brackets."; importNote!.dataset.state = "bad"; return; }
      const r = fromSupplied(key, v);
      if (!r.ok) { importNote!.textContent = `${r.message} Nothing was imported.`; importNote!.dataset.state = "bad"; return; }
      if (hasEntries(draft) && !replaceBox.checked) { importNote!.textContent = "You have entries above. Tick \"Replace what I have entered above\" to import over them; nothing was changed."; importNote!.dataset.state = "bad"; return; }
      Object.assign(draft, r.draft);
      const note = "Imported. Check every row below before composing: the composer will refuse anything that does not match the approved records.";
      render();
      importNote!.textContent = note; importNote!.dataset.state = "ok";
    } } }, "Import JSON into the form");
    return h(
      "details",
      { class: "sk-advanced", dataset: { advanced: "true" } },
      h("summary", null, "Advanced: view or import JSON"),
      h("p", { class: "fx-help" }, "The form above is all you need. This shows exactly what will be sent, and can load entries you prepared elsewhere (for example from an AI client) into the form. It never fills anything in on its own."),
      h("label", { for: `${base}-json` }, "What will be sent"), jsonView,
      h("label", null, "Import entries from JSON", input),
      hasEntries(draft) ? replaceRow : null,
      go,
      importNote,
    );
  }

  // ---- render ---------------------------------------------------------------------------------------------------------------------
  function render() {
    slots.clear(); controls.clear(); n = 0;
    const secs = sections();
    root.replaceChildren(
      h("h4", null, o.required ? "Entries you supply for this site" : "Entries you supply for this site (optional)"),
      h("p", { class: "muted" }, o.description, " Everything is recorded exactly as you write it in the saved revision. Nothing is added to it.", /client list/i.test(o.description) ? "" : " The client list is never read."),
      ...secs,
      advanced(),
    );
    refresh();
    if (focusAfter) { (root.querySelector(focusAfter) as HTMLElement | null)?.focus(); focusAfter = null; }
  }
  render();

  return {
    el: root,
    validate() { revealed = true; refresh(); return errors; },
    focusFirstProblem() {
      const first = [...slots.entries()].find(([, el]) => !el.hidden);
      const c = first ? controls.get(first[0]) : null;
      (c ?? first?.[1])?.focus?.();
      if (first && !c) first[1].scrollIntoView?.({ block: "center" });
    },
  };
}
